import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileText,
  Loader2,
  RefreshCw,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  ActionsTd,
  Attention,
  ExpandToggle,
  ExpandedRow,
  PayTable,
  SectionHeader,
  SubTabs,
  TableState,
  Td,
  Th,
} from '@/components/admin/PaymentsLayout';
import { cn } from '@/lib/utils';
import { formatUsd } from '@/lib/format';
import { fmtMirrorDate } from '@/lib/dates';
import {
  useRemittance,
  useRemittanceAction,
  useRemittances,
  type RemitStatus,
  type RemittanceRow,
} from '@/hooks/useRemittances';

/**
 * Remittances — what customers emailed to remit@ saying which invoices their
 * bank transfer or cheque pays.
 *
 * ⚠ This is not a payment ledger. Nothing here books, applies or settles
 * anything — QuickBooks does that when the money lands. What the queue
 * answers is "a lump ACH is coming, what does it cover", which a bank
 * transfer cannot tell us by itself.
 *
 * Rows close THEMSELVES: opening this panel re-reads the balances of every
 * invoice an open advice names, so anything QuickBooks has since applied
 * moves to Cleared without anyone clicking.
 */

const FILTERS: Array<{ key: RemitStatus | 'all'; label: string }> = [
  { key: 'needs_review', label: 'Needs a look' },
  { key: 'open', label: 'Money expected' },
  { key: 'cleared', label: 'Cleared' },
  { key: 'failed', label: "Couldn't read" },
  { key: 'all', label: 'Everything' },
];

const STATUS_STYLE: Record<string, string> = {
  needs_review: 'border-amber-200 bg-amber-50 text-amber-800',
  open: 'border-blue-200 bg-blue-50 text-blue-700',
  cleared: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  received: 'border-brix-border bg-brix-surface-alt text-brix-muted',
  failed: 'border-red-200 bg-red-50 text-red-700',
  rejected: 'border-red-200 bg-red-50 text-red-700',
};

const STATUS_LABEL: Record<string, string> = {
  needs_review: 'Needs a look',
  open: 'Money expected',
  cleared: 'Cleared',
  received: 'Just arrived',
  failed: "Couldn't read",
  rejected: 'Rejected',
};

export function RemittancesPanel() {
  const [filter, setFilter] = useState<RemitStatus | 'all'>('needs_review');
  const query = useRemittances(filter);
  // Counts for the sub-tabs that ask for action (the tab badge reads the first).
  const needsQ = useRemittances('needs_review');
  const openQ = useRemittances('open');
  const failedQ = useRemittances('failed');
  const act = useRemittanceAction();
  const rows = query.data?.remittances ?? [];
  const clearedNow = query.data?.cleared_now ?? 0;
  const needs = needsQ.data?.remittances.length ?? 0;
  const failed = failedQ.data?.remittances.length ?? 0;
  const [openId, setOpenId] = useState<string | null>(null);

  const countFor = (k: RemitStatus | 'all'): number | undefined =>
    k === 'needs_review' ? needsQ.data?.remittances.length
      : k === 'open' ? openQ.data?.remittances.length
        : k === 'failed' ? failedQ.data?.remittances.length
          : undefined;

  return (
    <div className="space-y-3">
      <SectionHeader
        description={
          <>
            What customers email to <span className="font-mono">remit@alamedapointbg.com</span> when
            they pay us by bank bill-pay, their own ACH, or a cheque. A bank transfer arrives as just
            an amount, so this is what tells us which invoices it covers.{' '}
            <span className="font-semibold">Nothing here books a payment</span> — QuickBooks applies
            the money, and a row clears itself once the mirror shows those invoices at zero.
          </>
        }
      />

      <SubTabs
        tabs={FILTERS.map((f) => ({ key: f.key, label: f.label, count: countFor(f.key) }))}
        value={filter}
        onChange={(k) => { setFilter(k); setOpenId(null); }}
        right={
          <Button
            variant="secondary"
            size="sm"
            disabled={query.isFetching}
            onClick={() => { void query.refetch(); void needsQ.refetch(); void openQ.refetch(); void failedQ.refetch(); }}
          >
            {query.isFetching ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh
          </Button>
        }
      />

      {(filter === 'needs_review' || filter === 'failed') && (
        <Attention count={needs + failed}>
          {needs > 0 ? `${needs} we couldn't match to an account or invoice` : ''}
          {needs > 0 && failed > 0 ? ' and ' : ''}
          {failed > 0 ? `${failed} we couldn't read` : ''}. Open the row, look at the email, then pick the account or enter it by hand.
        </Attention>
      )}

      {clearedNow > 0 && (
        <p className="flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          <CheckCircle2 size={14} />
          {clearedNow} cleared just now — QuickBooks has applied {clearedNow === 1 ? 'it' : 'them'}.
        </p>
      )}

      {act.isError && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {act.error.message}
        </p>
      )}

      <TableState
        loading={query.isLoading}
        error={query.isError ? query.error : null}
        empty={rows.length === 0}
        emptyText={filter === 'needs_review' ? 'Nothing needs a look.' : 'No remittances in this view.'}
      >
        <PayTable minWidth={900}>
          <thead>
            <tr>
              <Th className="w-8" />
              <Th>Received</Th>
              <Th>Payer · account</Th>
              <Th right>Amount</Th>
              <Th>Invoices</Th>
              <Th>Status</Th>
              <Th right>Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brix-border">
            {rows.map((r) => (
              <RemitRow
                key={r.id}
                row={r}
                busy={act.isPending}
                expanded={openId === r.id}
                onToggle={() => setOpenId(openId === r.id ? null : r.id)}
                onAction={(action, customer_id) => act.mutate({ id: r.id, action, customer_id })}
              />
            ))}
          </tbody>
        </PayTable>
      </TableState>
    </div>
  );
}

/**
 * One remittance: a table row, and — opened with the arrow — everything about
 * it: how we matched the account, the invoices it names, the email itself and
 * the hand-correction form.
 */
function RemitRow({
  row,
  busy,
  expanded,
  onToggle,
  onAction,
}: {
  row: RemittanceRow;
  busy: boolean;
  expanded: boolean;
  onToggle: () => void;
  onAction: (action: 'assign' | 'acknowledge' | 'dismiss' | 'reprocess', customerId?: string) => void;
}) {
  /* Two separate disclosures inside the opened row: `look` is the SOURCE — the
     email body and the attachment — which the list query cannot carry and which
     is the only thing to go on when the reader failed; `fixing` is the form. */
  const [look, setLook] = useState(false);
  const [fixing, setFixing] = useState(false);
  const detail = useRemittance(look || fixing ? row.id : null);
  const lines = row.invoices ?? [];
  const hits = lines.filter((l) => l.matched);
  const unmatched = row.unmatched ?? [];
  const needsWork = row.status === 'failed' || row.status === 'needs_review';

  const openWith = (fn: () => void) => {
    if (!expanded) onToggle();
    fn();
  };

  return (
    <>
      <tr className={expanded ? 'bg-brix-surface-alt/40' : undefined}>
        <Td className="pl-2 pr-0">
          <ExpandToggle open={expanded} label="remittance details" onClick={onToggle} />
        </Td>
        <Td className="whitespace-nowrap text-xs">
          {row.received_at ? new Date(row.received_at).toLocaleDateString() : '—'}
          {row.payment_method && <span className="block text-brix-muted">{row.payment_method.replace('_', ' ')}</span>}
        </Td>
        <Td>
          <span className="font-semibold text-slate-900">
            {row.payer_name ?? row.from_name ?? row.from_email ?? 'Unknown payer'}
          </span>
          <span className="block text-xs">
            {row.customer_id ? (
              <Link to={`/admin/customers/${row.customer_id}`} className="text-brix-primary hover:underline">
                {row.customer_name ?? 'this account'}
              </Link>
            ) : (
              <span className="font-semibold text-amber-800">No account matched</span>
            )}
          </span>
        </Td>
        <Td right mono>{row.amount_total !== null ? formatUsd(row.amount_total) : '—'}</Td>
        <Td className="text-xs">
          {hits.length} matched
          {unmatched.length > 0 && <span className="font-semibold text-amber-800"> · {unmatched.length} not found</span>}
        </Td>
        <Td>
          <span
            className={cn(
              'rounded-md border px-2 py-0.5 text-[11px] font-semibold',
              STATUS_STYLE[row.status] ?? STATUS_STYLE.received,
            )}
          >
            {STATUS_LABEL[row.status] ?? row.status}
          </span>
          {row.acknowledged_at && (
            <span className="mt-0.5 block text-[11px] text-brix-muted">handled by {row.acknowledged_by ?? 'staff'}</span>
          )}
        </Td>
        <ActionsTd>
          <Button size="sm" variant="secondary" onClick={() => openWith(() => setLook((v) => !v))}>
            {look && expanded ? 'Hide the email' : 'Look at it'}
          </Button>
          {needsWork && (
            <>
              <Button size="sm" variant="secondary" disabled={busy} onClick={() => onAction('reprocess')}>
                Read it again
              </Button>
              <Button size="sm" disabled={busy} onClick={() => openWith(() => setFixing((v) => !v))}>
                {fixing && expanded ? 'Cancel' : 'Enter by hand'}
              </Button>
            </>
          )}
          {!row.acknowledged_at && row.status !== 'cleared' && (
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => onAction('acknowledge')}>
              Mark handled
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            title="Clear from the queue (stays on record)"
            disabled={busy}
            onClick={() => {
              if (window.confirm('Clear this from the queue? It stays on record.')) onAction('dismiss');
            }}
          >
            <X size={14} />
          </Button>
        </ActionsTd>
      </tr>

      {expanded && (
        <ExpandedRow colSpan={7}>
          <div className="space-y-2">
            <p className="text-xs text-brix-muted">
              {row.received_at ? new Date(row.received_at).toLocaleString() : '—'}
              {row.from_email ? ` · ${row.from_email}` : ''}
              {row.payment_ref ? ` · ref ${row.payment_ref}` : ''}
              {row.payment_date ? ` · sent ${fmtMirrorDate(row.payment_date)}` : ''}
              {row.attachment_name ? ` · attachment ${row.attachment_name}` : ''}
            </p>

            {/* Whose account, and HOW we decided — a matched account with no
                stated reason is not something a reviewer can confirm. */}
            {row.customer_id && row.identity_note && (
              <p className="text-xs text-brix-muted">Account: {row.identity_note}</p>
            )}

            {row.disposition && (
              <p className="flex items-start gap-1.5 text-xs leading-relaxed text-brix-muted">
                {needsWork ? (
                  <AlertTriangle size={12} className="mt-0.5 shrink-0 text-amber-600" />
                ) : (
                  <Clock size={12} className="mt-0.5 shrink-0" />
                )}
                {row.disposition}
              </p>
            )}

            {/* Candidate accounts when the sender's address is shared across a
                chain — the reviewer picks; nothing was guessed. */}
            {!row.customer_id && (row.candidates ?? []).length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-semibold text-brix-muted">Could be:</p>
                {(row.candidates ?? []).slice(0, 8).map((c) => (
                  <div key={c.customerId} className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-slate-800">{c.name}</span>
                    <Button size="sm" variant="secondary" disabled={busy} onClick={() => onAction('assign', c.customerId)}>
                      It's this one
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {(lines.length > 0 || unmatched.length > 0) && (
              <div className="overflow-x-auto rounded-lg border border-brix-border bg-white">
                <table className="w-full text-xs">
                  <thead className="bg-brix-surface-alt/60 text-brix-muted">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-semibold">Invoice</th>
                      <th className="px-2 py-1.5 text-right font-semibold">They applied</th>
                      <th className="px-2 py-1.5 text-right font-semibold">Balance now</th>
                      <th className="px-2 py-1.5 text-left font-semibold">State</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l, i) => (
                      <tr key={`${l.invoice_number}-${i}`} className="border-t border-brix-border">
                        <td className="px-2 py-1.5 font-mono text-slate-900">{l.doc_number ?? l.invoice_number}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{l.amount === null ? '—' : formatUsd(l.amount)}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{l.balance === null ? '—' : formatUsd(l.balance)}</td>
                        <td className="px-2 py-1.5">
                          {l.ambiguous ? (
                            <span className="font-semibold text-amber-800">matches more than one</span>
                          ) : !l.matched ? (
                            <span className="font-semibold text-amber-800">not found</span>
                          ) : l.paid ? (
                            <span className="text-emerald-700">applied in QuickBooks</span>
                          ) : (
                            <span className="text-blue-700">still open</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {unmatched
                      .filter((n) => !lines.some((l) => l.invoice_number === n))
                      .map((n) => (
                        <tr key={`u-${n}`} className="border-t border-brix-border">
                          <td className="px-2 py-1.5 font-mono text-slate-900">{n}</td>
                          <td className="px-2 py-1.5 text-right">—</td>
                          <td className="px-2 py-1.5 text-right">—</td>
                          <td className="px-2 py-1.5 font-semibold text-amber-800">not found</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}

            {look && <SourcePanel detail={detail} row={row} />}
            {fixing && (
              <CorrectByHand row={row} detail={detail} busy={busy} onDone={() => setFixing(false)} />
            )}
          </div>
        </ExpandedRow>
      )}
    </>
  );
}

/* ---------------- the source ---------------- */

type Detail = ReturnType<typeof useRemittance>;

/**
 * What the customer actually sent.
 *
 * ⚠ This is the half that was missing. The bell said "remittances to sort out"
 * and the row said "we couldn't read this" — and there was no way to READ IT
 * YOURSELF. The endpoint has always returned the email body and a signed URL
 * for the attachment on its `?id=` branch; nothing on screen ever asked for
 * them. An advice we failed to parse has nothing else to go on.
 */
function SourcePanel({ detail, row }: { detail: Detail; row: RemittanceRow }) {
  const d = detail.data?.remittance;
  const url = detail.data?.document_url ?? null;

  return (
    <div className="mt-3 rounded-xl border border-brix-border bg-slate-50 p-3">
      {detail.isLoading ? (
        <p className="text-xs text-brix-muted">Fetching the email…</p>
      ) : detail.error ? (
        /* A failed read here must not look like an empty email. */
        <p className="text-xs text-red-700">
          Couldn&apos;t load it:{' '}
          {detail.error instanceof Error ? detail.error.message : 'please try again'}
        </p>
      ) : (
        <>
          <dl className="mb-3 grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
            <Fact label="From" value={row.from_email ?? '—'} />
            <Fact label="Subject" value={d?.subject ?? '—'} />
            <Fact label="Received" value={row.received_at ? fmtMirrorDate(row.received_at) : '—'} />
            <Fact
              label="Attachment"
              value={row.attachment_name ?? 'none — the advice was in the email body'}
            />
          </dl>

          {/* ⚠ The document IS the advice. Sky: "they will most likely all get
              attachments to read in the email" — so a link that opens a new tab
              is the wrong shape: you are comparing the document against the
              matched invoices right here. Render it inline and keep the link
              for the cases an embed can't serve (a browser that blocks PDF
              framing, or someone who wants it full-screen). */}
          {url && (
            <div className="mb-3">
              <div className="mb-1 flex items-center justify-between gap-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-brix-muted">
                  The document
                </p>
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-brix-primary hover:underline"
                >
                  <FileText size={12} /> Open {row.attachment_name ?? 'it'} in a new tab
                </a>
              </div>
              {isImageName(row.attachment_name) ? (
                <img
                  src={url}
                  alt={row.attachment_name ?? 'remittance advice'}
                  className="max-h-[32rem] w-full rounded-lg border border-brix-border bg-white object-contain"
                />
              ) : (
                <iframe
                  src={url}
                  title={row.attachment_name ?? 'remittance advice'}
                  className="h-[32rem] w-full rounded-lg border border-brix-border bg-white"
                />
              )}
            </div>
          )}

          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-brix-muted">
            The email
          </p>
          {d?.raw_text ? (
            /* Pre-wrapped and scrolled rather than truncated: a remittance body
               is a table in plain text, and reflowing it destroys the columns
               that make it readable. */
            <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-brix-border bg-white p-3 text-[11px] leading-relaxed text-brix-ink">
              {d.raw_text}
            </pre>
          ) : (
            <p className="text-xs text-brix-muted">
              No body text was captured — whatever they sent is in the attachment above.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="shrink-0 font-semibold text-brix-muted">{label}</dt>
      <dd className="min-w-0 break-words text-brix-ink">{value}</dd>
    </div>
  );
}

/* ---------------- enter it by hand ---------------- */

/**
 * The other half. "Read it again" only re-runs the same reader on the same
 * document — useful after a transient failure, useless when the document is a
 * scan the model genuinely cannot parse. A person looking at it can type the
 * three things that matter in ten seconds.
 *
 * ⚠ It records a CLAIM, and the panel says so. Nothing here books, applies or
 * settles a payment: `cleared` still comes only from QuickBooks showing every
 * invoice it names at zero.
 */
function CorrectByHand({
  row,
  detail,
  busy,
  onDone,
}: {
  row: RemittanceRow;
  detail: Detail;
  busy: boolean;
  onDone: () => void;
}) {
  const act = useRemittanceAction();
  const [invoiceText, setInvoiceText] = useState('');
  const [ref, setRef] = useState(row.payment_ref ?? '');
  const [date, setDate] = useState(row.payment_date ?? '');
  const [method, setMethod] = useState(row.payment_method ?? '');
  const [amount, setAmount] = useState(row.amount_total != null ? String(row.amount_total) : '');
  const [err, setErr] = useState<string | null>(null);

  const nothingTyped = !invoiceText.trim() && !ref.trim() && !amount.trim();

  async function save() {
    setErr(null);
    try {
      await act.mutateAsync({
        id: row.id,
        action: 'correct',
        invoice_text: invoiceText.trim() || undefined,
        payment_ref: ref.trim() || undefined,
        payment_date: date || undefined,
        payment_method: method.trim() || undefined,
        amount_total: amount.trim() || undefined,
      });
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'That did not save.');
    }
  }

  return (
    <div className="mt-3 rounded-xl border border-brix-primary/30 bg-brix-primary-tint/40 p-3">
      <p className="mb-1 text-xs font-semibold text-brix-ink">Enter what it says</p>
      <p className="mb-3 text-[11px] leading-relaxed text-brix-muted">
        Read the invoice numbers off the document and type them here — commas, spaces
        or new lines all work, and you can paste the whole thing. We&apos;ll match them
        against{' '}
        {row.customer_name ? <span className="font-semibold">{row.customer_name}</span> : 'the account once one is set'}
        . This records what they told us; it does not mark anything paid — that still
        comes from QuickBooks.
      </p>

      {!detail.data?.remittance.raw_text && !detail.data?.document_url && detail.isLoading && (
        <p className="mb-2 text-[11px] text-brix-muted">Loading the document to read from…</p>
      )}

      <textarea
        value={invoiceText}
        onChange={(e) => setInvoiceText(e.target.value)}
        rows={2}
        placeholder="9989019630, BTRF-0626-628"
        className="mb-2 w-full rounded-lg border border-brix-border px-3 py-2 font-mono text-xs"
      />

      <div className="mb-3 grid gap-2 sm:grid-cols-4">
        <input
          value={ref}
          onChange={(e) => setRef(e.target.value)}
          placeholder="Cheque / ref #"
          className="rounded-lg border border-brix-border px-3 py-2 text-xs"
        />
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="rounded-lg border border-brix-border px-3 py-2 text-xs"
        />
        <input
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          placeholder="ACH / cheque"
          className="rounded-lg border border-brix-border px-3 py-2 text-xs"
        />
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Total $"
          className="rounded-lg border border-brix-border px-3 py-2 text-right font-mono text-xs"
        />
      </div>

      {err && <p className="mb-2 text-xs font-medium text-red-700">{err}</p>}

      <div className="flex items-center gap-2">
        <Button size="sm" disabled={busy || act.isPending || nothingTyped} onClick={save}>
          {act.isPending ? 'Saving…' : 'Save and match'}
        </Button>
        <button
          type="button"
          onClick={onDone}
          className="text-xs text-brix-muted hover:underline"
        >
          Cancel
        </button>
        {nothingTyped && (
          <span className="text-[11px] text-brix-muted">
            Type the invoice numbers, or at least the reference and amount.
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * A scan comes in as an image, an advice from an accounting system as a PDF.
 * An <img> cannot show a PDF and an <iframe> shows an image at its natural
 * size with no fit, so the two need different elements. Extension-based
 * because that is what the row carries — the stored content type is not on it.
 */
function isImageName(name: string | null | undefined): boolean {
  return /\.(png|jpe?g|gif|webp|heic|bmp|tiff?)$/i.test(String(name ?? ''));
}
